import { httpClient } from '@/core/api/httpClient';
import { API_V1 } from '@/core/api/endpoints';
import type {
  ProjectFileDto,
  ProjectFolderNodeDto,
  CreatedResponse,
  CreateProjectFolderInput,
  MoveProjectFolderInput,
  RenameProjectFolderInput,
  MoveProjectFileInput,
  RenameProjectFileInput,
  GetFilesParams,
  SectionCounts,
  BulkDeleteResponse,
  FilePreviewUrlDto,
} from '../types';

export const projectFilesService = {
  getFiles: (projectId: string, params: GetFilesParams = {}, signal?: AbortSignal) => {
    const { folderId, section, sortBy, sortDir } = params;
    const query: Record<string, string> = {};
    if (folderId) query.folderId = folderId;
    if (section && section !== 'all') query.section = section;
    if (sortBy) query.sortBy = sortBy;
    if (sortDir) query.sortDir = sortDir;
    return httpClient.get<ProjectFileDto[]>(`${API_V1}/projects/${projectId}/files`, {
      params: Object.keys(query).length ? query : undefined,
      signal,
    });
  },

  getFile: (projectId: string, fileId: string, signal?: AbortSignal) =>
    httpClient.get<ProjectFileDto>(`${API_V1}/projects/${projectId}/files/${fileId}`, { signal }),

  getFilePreviewUrl: (projectId: string, fileId: string, signal?: AbortSignal) =>
    httpClient.get<FilePreviewUrlDto>(
      `${API_V1}/projects/${projectId}/files/${fileId}/preview-url`,
      { signal },
    ),

  getFileCounts: (projectId: string, signal?: AbortSignal) =>
    httpClient.get<SectionCounts>(`${API_V1}/projects/${projectId}/files/counts`, { signal }),

  /**
   * Uploads a file to a project.
   *
   * @param source - Marks where the upload came from, e.g. `'whiteboard'`. Sent as a query
   * param alongside `folderId`, matching how this endpoint already receives its non-file
   * fields. Note the value comes back from the API PascalCased (`'Whiteboard'`), so compare
   * `ProjectFileDto.source` against that casing, not the one sent here.
   */
  uploadFile: (projectId: string, file: File, folderId?: string, source?: string) => {
    const formData = new FormData();
    formData.append('file', file);

    const params: Record<string, string> = {};
    if (folderId) params.folderId = folderId;
    if (source) params.source = source;

    return httpClient.post<FormData, CreatedResponse>(
      `${API_V1}/projects/${projectId}/files`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: Object.keys(params).length > 0 ? params : undefined,
      },
    );
  },

  deleteFile: (projectId: string, fileId: string) =>
    httpClient.delete(`${API_V1}/projects/${projectId}/files/${fileId}`),

  bulkDeleteFiles: (projectId: string, fileIds: string[]) =>
    httpClient.delete<BulkDeleteResponse>(`${API_V1}/projects/${projectId}/files`, {
      data: { fileIds },
    }),

  moveFile: (projectId: string, fileId: string, data: MoveProjectFileInput) =>
    httpClient.put(`${API_V1}/projects/${projectId}/files/${fileId}/move`, data),

  renameFile: (projectId: string, fileId: string, data: RenameProjectFileInput) =>
    httpClient.put(`${API_V1}/projects/${projectId}/files/${fileId}/rename`, data),

  toggleFavorite: (projectId: string, fileId: string, isFavorite: boolean) =>
    httpClient.put(`${API_V1}/projects/${projectId}/files/${fileId}/favorite`, { isFavorite }),

  setVisibility: (projectId: string, fileId: string, isPrivate: boolean) =>
    httpClient.put(`${API_V1}/projects/${projectId}/files/${fileId}/visibility`, { isPrivate }),

  getFolders: (projectId: string, signal?: AbortSignal) =>
    httpClient.get<ProjectFolderNodeDto[]>(`${API_V1}/projects/${projectId}/folders`, { signal }),

  createFolder: (projectId: string, data: CreateProjectFolderInput) =>
    httpClient.post<CreateProjectFolderInput, CreatedResponse>(
      `${API_V1}/projects/${projectId}/folders`,
      data,
    ),

  deleteFolder: (projectId: string, folderId: string) =>
    httpClient.delete(`${API_V1}/projects/${projectId}/folders/${folderId}`),

  moveFolder: (projectId: string, folderId: string, data: MoveProjectFolderInput) =>
    httpClient.put(`${API_V1}/projects/${projectId}/folders/${folderId}/move`, data),

  renameFolder: (projectId: string, folderId: string, data: RenameProjectFolderInput) =>
    httpClient.put(`${API_V1}/projects/${projectId}/folders/${folderId}/rename`, data),
};
